/**
 * Specialized activity cell renderers for audit logs.
 * Extracted from table-columns.tsx to improve modularity and testability.
 */

import React from 'react';
import { Text, Tooltip, Link } from '@customerio/pluma-components/react';
import type { AuditLogEntry } from '../../types';
import { formatAttributeName, lowercasePreservingAcronyms } from '../../utils/formatting';
import { getUserDisplayNameWithEmail } from '../../utils/user-formatting';
import { buildEntityUrl } from '../../utils/entity-urls';

/**
 * Render a user name with email where only the name is bolded.
 * If no name exists, the email is bolded instead.
 *
 * @param name - The user's full name (or null if not available)
 * @param email - The user's email
 * @param suffix - Optional suffix to append (e.g., "'s" for possessive form)
 * @returns React node with proper formatting
 */
export function renderUserNameWithEmail({
  name,
  email,
  suffix = '',
}: {
  name: string | null;
  email: string;
  suffix?: string;
}): React.ReactNode {
  if (name != null && name !== '') {
    // Has name: bold name, normal email in parentheses
    return (
      <>
        <Text as="span" size="s" weight="medium">
          {name}
        </Text>
        {` (${email})`}
        {suffix}
      </>
    );
  }

  // No name: bold email
  return (
    <>
      <Text as="span" size="s" weight="medium">
        {email}
      </Text>
      {suffix}
    </>
  );
}

/**
 * Get name and email parts from a user object for use with renderUserNameWithEmail.
 */
export function getUserNameAndEmail(user: { email: string; first_name?: string; last_name?: string }): {
  name: string | null;
  email: string;
} {
  const firstName = user.first_name?.trim() ?? '';
  const lastName = user.last_name?.trim() ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return {
    name: fullName !== '' ? fullName : null,
    email: user.email,
  };
}

/**
 * Common style for text that should truncate with ellipsis.
 */
export const ELLIPSIS_TEXT_STYLE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
} as const;

/**
 * Timestamp fields to exclude from changed fields display.
 * These fields always change on updates and aren't meaningful to show.
 * Field names are normalized (lowercased, underscores/hyphens removed) for comparison.
 */
const EXCLUDED_TIMESTAMP_FIELDS = new Set([
  'created',
  'createdat',
  'updated',
  'updatedat',
  'lastupdated',
  'emailchangedat',
]);

/**
 * Check if a field name is an excluded timestamp field.
 * Normalizes the field name before comparison.
 */
export function isExcludedTimestampField(field: string): boolean {
  return EXCLUDED_TIMESTAMP_FIELDS.has(field.toLowerCase().replace(/[_-]/g, ''));
}

/**
 * Get passive verb for system events based on action.
 */
export function getPassiveVerb(action: string): string {
  const actionLower = action.toLowerCase();
  if (actionLower === 'created') return 'been added';
  if (actionLower === 'deleted') return 'been deleted';
  return 'been updated';
}

/**
 * Render a system event activity cell with passive voice.
 * Used for "The X has been added/updated/deleted" patterns.
 */
export function renderSystemEventActivity({
  entity,
  passiveVerb,
  hasHave = 'has',
  suffix,
  summaryText,
}: {
  entity: string | React.ReactNode;
  passiveVerb: string;
  hasHave?: 'has' | 'have';
  suffix?: React.ReactNode;
  summaryText?: string;
}): React.ReactNode {
  // Use explicit summaryText if provided, otherwise derive from entity string
  const entityText = summaryText ?? (typeof entity === 'string' ? entity : 'Entity');
  const summary = `${entityText} ${hasHave} ${passiveVerb}`;

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {typeof entity === 'string' ? (
          <Text as="span" size="s" weight="medium">
            {entity}
          </Text>
        ) : (
          entity
        )}{' '}
        {hasHave} {passiveVerb}
        {suffix}
      </Text>
    </Tooltip>
  );
}

// Common context passed to all renderers
export interface ActivityRenderContext {
  log: AuditLogEntry;
  /** Full display name for tooltips (e.g., "John Doe (john@example.com)") */
  userName: string;
  /** Actor's name part only (null if no name, only email) */
  actorName: string | null;
  /** Actor's email */
  actorEmail: string | null;
  verb: string;
  objectName: string;
  displayObjectName: string;
  usersById: Map<
    number,
    { user_id: number; email: string; first_name?: string; last_name?: string; deleted?: boolean }
  >;
  roleDefinitions?: Record<number, { id: number; name: string }> | null;
  segments: Array<{ id: number; name: string }>;
  workspaces: Array<{ id: number; name: string }>;
  newsletters?: Array<{ id: number; name: string }>;
  contextId: number;
}

/**
 * Render the actor's name with email (name bold, email not bold).
 * If no name exists, email is bolded.
 */
export function renderActorName(ctx: ActivityRenderContext): React.ReactNode {
  const { actorName, actorEmail, userName } = ctx;

  // If we have email, use the formatted rendering
  if (actorEmail != null) {
    return renderUserNameWithEmail({
      name: actorName,
      email: actorEmail,
    });
  }

  // Fallback: bold the whole userName (e.g., "System" or unknown user)
  return (
    <Text as="span" size="s" weight="medium">
      {userName}
    </Text>
  );
}

/**
 * Get the best data source from a log entry.
 * Uses V2 API fields: changed_to for creates/updates, changed_from for deletes.
 * Falls back to data field for backwards compatibility.
 */
export function getLogData(log: AuditLogEntry): Record<string, unknown> | null {
  const actionLower = log.action?.toLowerCase() ?? '';

  // For deletes, use changed_from
  if (actionLower === 'deleted') {
    if (log.changed_from != null && typeof log.changed_from === 'object' && Object.keys(log.changed_from).length > 0) {
      return log.changed_from as Record<string, unknown>;
    }
    // Fallback to data field for backwards compatibility
    if (
      log.data != null &&
      typeof log.data === 'object' &&
      !Array.isArray(log.data) &&
      Object.keys(log.data).length > 0
    ) {
      return log.data as Record<string, unknown>;
    }
    return null;
  }

  // For creates/updates, prefer changed_to, fall back to changed_from for updates
  if (log.changed_to != null && typeof log.changed_to === 'object' && Object.keys(log.changed_to).length > 0) {
    return log.changed_to as Record<string, unknown>;
  }

  // For updates, also try changed_from if changed_to is empty
  if (
    actionLower === 'updated' &&
    log.changed_from != null &&
    typeof log.changed_from === 'object' &&
    Object.keys(log.changed_from).length > 0
  ) {
    return log.changed_from as Record<string, unknown>;
  }

  // Fallback to data field for backwards compatibility
  if (
    log.data != null &&
    typeof log.data === 'object' &&
    !Array.isArray(log.data) &&
    Object.keys(log.data).length > 0
  ) {
    return log.data as Record<string, unknown>;
  }

  return null;
}

/**
 * Mapping of Design Studio node type values to user-friendly names.
 */
const DESIGN_STUDIO_TYPE_LABELS: Record<string, string> = {
  folder: 'Design Studio Folder',
  component: 'Design Studio Component',
  template: 'Design Studio Template',
  email: 'Design Studio Email',
};

/**
 * Get the specific Design Studio element type from a DesignStudioNode log entry.
 * Returns a user-friendly name like "Design Studio Email" or "Design Studio Component".
 * Falls back to "Design Studio Element" if type is not available or recognized.
 *
 * @param log - The audit log entry
 * @returns User-friendly Design Studio element type name
 */
export function getDesignStudioElementType(log: AuditLogEntry): string {
  if (log.object_type !== 'DesignStudioNode') {
    return 'Design Studio Element';
  }

  const data = getLogData(log);
  if (data == null) {
    return 'Design Studio Element';
  }

  const typeValue = typeof data.type === 'string' ? data.type.toLowerCase() : null;
  if (typeValue != null && DESIGN_STUDIO_TYPE_LABELS[typeValue] != null) {
    return DESIGN_STUDIO_TYPE_LABELS[typeValue];
  }

  return 'Design Studio Element';
}

/**
 * Extract team member info from User event payload.
 * Uses V2 API fields when available.
 * Returns userId to enable self-update detection.
 */
export function getTeamMemberInfo(log: AuditLogEntry): {
  name: string | null;
  email: string | null;
  userId: number | null;
} {
  if (log.object_type !== 'User') {
    return { name: null, email: null, userId: null };
  }
  const data = getLogData(log);
  if (data == null) {
    return { name: null, email: null, userId: null };
  }
  const firstName = typeof data.first_name === 'string' ? data.first_name : '';
  const lastName = typeof data.last_name === 'string' ? data.last_name : '';
  const fullName = [firstName, lastName].filter((s) => s !== '').join(' ');
  const email = typeof data.email === 'string' ? data.email : null;
  const userId = typeof data.id === 'number' ? data.id : null;
  return { name: fullName !== '' ? fullName : null, email, userId };
}

/**
 * Get permission target member info from permission event payload.
 * Returns displayName, name, and email to enable proper formatting (name bold, email not).
 * Uses V2 API fields when available.
 */
export function getPermissionTargetMember(
  log: AuditLogEntry,
  usersById: ActivityRenderContext['usersById'],
): { displayName: string; name: string | null; email: string } | null {
  const data = getLogData(log);
  if (data == null) {
    return null;
  }

  // Try to get user_id from different possible locations
  let targetUserId: number | null = null;

  if (typeof data.user_id === 'number') {
    targetUserId = data.user_id;
  } else if (Array.isArray(data.user_ids) === true && (data.user_ids as unknown[]).length > 0) {
    const firstUserId = (data.user_ids as unknown[])[0];
    if (typeof firstUserId === 'number') {
      targetUserId = firstUserId;
    }
  } else if (Array.isArray(data.roles) === true && (data.roles as unknown[]).length > 0) {
    const firstRole = (data.roles as unknown[])[0] as Record<string, unknown> | undefined;
    if (firstRole != null && typeof firstRole.user_id === 'number') {
      targetUserId = firstRole.user_id;
    }
  }

  if (targetUserId == null) {
    return null;
  }
  const user = usersById.get(targetUserId);
  if (user == null) {
    return null;
  }

  // Get name and email parts separately for proper formatting
  const { name, email } = getUserNameAndEmail(user);

  return {
    displayName: getUserDisplayNameWithEmail({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
    }),
    name,
    email,
  };
}

/**
 * Extract string from either a direct string or a nullable database field object.
 * The backend returns nullable string fields in the format:
 *   { string: "actual value", valid: true }  - when field has a value
 *   { string: "", valid: false }             - when field is NULL in database
 */
export function extractString(value: unknown): string | null {
  if (typeof value === 'string' && value !== '') {
    return value;
  }
  if (typeof value === 'object' && value != null) {
    const obj = value as Record<string, unknown>;
    if (obj.valid === true && typeof obj.string === 'string' && obj.string !== '') {
      return obj.string;
    }
  }
  return null;
}

/**
 * Get entity name from log data.
 * Uses V2 API fields when available.
 *
 * This function works for ALL object types that have a 'name' field in their data.
 * It checks multiple sources in order:
 * 1. changed_to/changed_from (via getLogData) - for creates/deletes
 * 2. Original data field - for updates where changed_to doesn't include name
 * 3. Array lookups - for specific types where we have pre-loaded data (Campaign, Segment, Newsletter, TransactionalMessage)
 */
export function getEntityName(
  log: AuditLogEntry,
  segments: Array<{ id: number; name: string }>,
  newsletters?: Array<{ id: number; name: string }>,
  transactionalMessages?: Array<{ id: number; name: string }>,
  campaigns?: Array<{ id: number; name: string }>,
): string | null {
  // For Segment objects, prioritize segments array lookup (most reliable source)
  // This ensures we get the current segment name even if the log data is outdated
  if (log.object_type === 'Segment' && log.object_id != null && segments.length > 0) {
    const segmentId = Number(log.object_id);
    if (!Number.isNaN(segmentId)) {
      // Handle both string and number IDs in segments array
      const segment = segments.find((s) => {
        const sId = typeof s.id === 'string' ? Number.parseInt(s.id, 10) : Number(s.id);
        return !Number.isNaN(sId) && sId === segmentId;
      });
      if (segment != null && segment.name != null && segment.name !== '') {
        return segment.name;
      }
    }
  }

  // Use getLogData to get the best data source (V2 API preferred)
  const data = getLogData(log);
  if (data != null) {
    // For Domain objects, prefer mailgun_domain over name
    if (log.object_type === 'Domain') {
      const mailgunDomain = extractString(data.mailgun_domain);
      if (mailgunDomain != null) {
        return mailgunDomain;
      }
    }

    // Generic check for 'name' field - works for ALL object types (Template, Layout, Snippet, Role, etc.)
    const nameValue = extractString(data.name);
    if (nameValue != null) {
      return nameValue;
    }
    // For Campaign, the name might be nested in a 'campaign' object
    if (typeof data.campaign === 'object' && data.campaign != null) {
      const campaign = data.campaign as Record<string, unknown>;
      const campaignName = extractString(campaign.name);
      if (campaignName != null) {
        return campaignName;
      }
    }
  }

  // Fallback: For updates, changed_to/changed_from may not have the name field.
  // Check the original data field which contains the full object.
  // This works for ALL object types that have a 'name' field.
  if (log.data != null && typeof log.data === 'object' && !Array.isArray(log.data)) {
    const originalData = log.data as Record<string, unknown>;
    // For Domain objects, prefer mailgun_domain over name
    if (log.object_type === 'Domain') {
      const mailgunDomain = extractString(originalData.mailgun_domain);
      if (mailgunDomain != null) {
        return mailgunDomain;
      }
    }

    // Generic check for 'name' field - works for ALL object types (Template, Layout, Snippet, Role, etc.)
    const nameValue = extractString(originalData.name);
    if (nameValue != null) {
      return nameValue;
    }
    // For Campaign, the name might be nested in a 'campaign' object
    if (typeof originalData.campaign === 'object' && originalData.campaign != null) {
      const campaign = originalData.campaign as Record<string, unknown>;
      const campaignName = extractString(campaign.name);
      if (campaignName != null) {
        return campaignName;
      }
    }
  }

  // For Campaign objects, look up name from campaigns array
  // Use Number() comparison to handle string/number type mismatches
  if (log.object_type === 'Campaign' && log.object_id != null && campaigns != null && campaigns.length > 0) {
    const campaignId = Number(log.object_id);
    const campaign = campaigns.find((c) => Number(c.id) === campaignId);
    if (campaign != null) {
      return campaign.name;
    }
  }

  // For Newsletter objects, look up name from newsletters array
  // Use Number() comparison to handle string/number type mismatches
  if (log.object_type === 'Newsletter' && log.object_id != null && newsletters != null && newsletters.length > 0) {
    const newsletterId = Number(log.object_id);
    const newsletter = newsletters.find((n) => Number(n.id) === newsletterId);
    if (newsletter != null) {
      return newsletter.name;
    }
  }

  // For TransactionalMessage objects, look up name from transactional messages array
  // Use Number() comparison to handle string/number type mismatches
  if (
    log.object_type === 'TransactionalMessage' &&
    log.object_id != null &&
    transactionalMessages != null &&
    transactionalMessages.length > 0
  ) {
    const transactionalMessageId = Number(log.object_id);
    const transactionalMessage = transactionalMessages.find((t) => Number(t.id) === transactionalMessageId);
    if (transactionalMessage != null) {
      return transactionalMessage.name;
    }
  }

  return null;
}

/**
 * Extract text content from a React node.
 * Recursively traverses React elements to extract all text content.
 */
function extractTextFromReactNode(node: React.ReactNode): string {
  if (node == null) {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextFromReactNode).join('');
  }
  if (React.isValidElement(node)) {
    // Extract text from children
    if (node.props.children != null) {
      return extractTextFromReactNode(node.props.children);
    }
  }
  return '';
}

/**
 * Format the object identifier - prefer name over ID when available.
 * Returns a React node with a link if the entity is linkable, otherwise a string.
 * Uses V2 API fields when available.
 * For CampaignAction, includes the parent campaign name when available.
 */
export function getObjectIdentifier(
  log: AuditLogEntry,
  entityName: string | null,
  contextId: number,
  campaigns?: Array<{ id: number; name: string }>,
): string | React.ReactNode {
  const objectId = log.object_id;
  const isLinkable = ['Campaign', 'Newsletter', 'TransactionalMessage', 'Segment'].includes(log.object_type);
  const url =
    isLinkable && contextId != null && contextId !== 0 ? buildEntityUrl(log.object_type, objectId, contextId) : null;

  // For CampaignAction, get the parent campaign name
  // campaign_id can be in log.data.data (nested structure) or in changed_to/changed_from
  let campaignSuffix: React.ReactNode = null;
  if (log.object_type === 'CampaignAction') {
    // Try multiple sources for campaign_id
    let campaignId: number | undefined;

    // First, try log.data.data (nested structure from API response)
    const logData = log.data as Record<string, unknown> | undefined;
    if (logData != null) {
      const nestedData = logData.data as Record<string, unknown> | undefined;
      if (nestedData != null && typeof nestedData.campaign_id === 'number') {
        campaignId = nestedData.campaign_id;
      }
      // Also try direct access (in case structure is different)
      if (campaignId == null && typeof logData.campaign_id === 'number') {
        campaignId = logData.campaign_id;
      }
    }

    // Fallback: try changed_to (for updates)
    if (campaignId == null && log.changed_to != null && typeof log.changed_to === 'object') {
      const changedTo = log.changed_to as Record<string, unknown>;
      if (typeof changedTo.campaign_id === 'number') {
        campaignId = changedTo.campaign_id;
      }
    }

    // Fallback: try changed_from (for deletes or if changed_to is empty)
    if (campaignId == null && log.changed_from != null && typeof log.changed_from === 'object') {
      const changedFrom = log.changed_from as Record<string, unknown>;
      if (typeof changedFrom.campaign_id === 'number') {
        campaignId = changedFrom.campaign_id;
      }
    }

    // Look up campaign name (or show ID if campaigns not loaded or campaign not found)
    if (campaignId != null && campaignId > 0) {
      const campaignUrl = buildEntityUrl('Campaign', String(campaignId), contextId);

      // Try to find campaign in the list if available
      // Use loose comparison to handle string/number type mismatches
      const campaign =
        campaigns != null && campaigns.length > 0 ? campaigns.find((c) => Number(c.id) === Number(campaignId)) : null;

      // Campaign found - show name with link, otherwise show ID as fallback
      campaignSuffix =
        campaign != null ? (
          <>
            {' from campaign "'}
            {campaignUrl != null ? (
              <Link href={campaignUrl} isExternal={true} variant="secondary" isUnderlined={false}>
                {campaign.name}
              </Link>
            ) : (
              campaign.name
            )}
            {'"'}
          </>
        ) : (
          <>
            {' from campaign '}
            {campaignUrl != null ? (
              <Link href={campaignUrl} isExternal={true} variant="secondary" isUnderlined={false}>
                #{campaignId}
              </Link>
            ) : (
              `#${campaignId}`
            )}
          </>
        );
    }
  }

  if (entityName != null) {
    if (url != null) {
      return (
        <>
          {'"'}
          <Link href={url} isExternal={true} variant="secondary" isUnderlined={false}>
            {entityName}
          </Link>
          {'"'}
          {campaignSuffix}
        </>
      );
    }
    // Return string if no campaign suffix, React node otherwise
    if (campaignSuffix == null) {
      return `"${entityName}"`;
    }
    return (
      <>
        {`"${entityName}"`}
        {campaignSuffix}
      </>
    );
  }

  // Fallback: try to get id from data (using V2 API preferred)
  const data = getLogData(log);
  let displayId: string | number | null = null;
  if (data != null) {
    const dataId = data.id ?? data.Id;
    if (dataId != null && (typeof dataId === 'string' || typeof dataId === 'number')) {
      displayId = dataId;
    }
  }
  if (displayId == null && objectId != null) {
    displayId = objectId;
  }

  if (displayId != null) {
    const idString = typeof displayId === 'string' ? displayId : String(displayId);
    if (url != null) {
      return (
        <>
          {'#'}
          <Link href={url} isExternal={true} variant="secondary" isUnderlined={false}>
            {idString}
          </Link>
          {campaignSuffix}
        </>
      );
    }
    // Return string if no campaign suffix, React node otherwise
    if (campaignSuffix == null) {
      return `#${idString}`;
    }
    return (
      <>
        {`#${idString}`}
        {campaignSuffix}
      </>
    );
  }

  return campaignSuffix ?? '';
}

/**
 * Compute changed fields text for updated actions.
 * Uses V2 API fields (changed_from/changed_to) provided by the backend.
 */
export function getChangedFieldsText(log: AuditLogEntry): string {
  // Get field names from changed_to and changed_from (V2 API)
  const hasV2ChangedTo =
    log.changed_to != null && typeof log.changed_to === 'object' && Object.keys(log.changed_to).length > 0;
  const hasV2ChangedFrom =
    log.changed_from != null && typeof log.changed_from === 'object' && Object.keys(log.changed_from).length > 0;

  if (!hasV2ChangedTo && !hasV2ChangedFrom) {
    return '';
  }

  const changedToKeys = hasV2ChangedTo ? Object.keys(log.changed_to as Record<string, unknown>) : [];
  const changedFromKeys = hasV2ChangedFrom ? Object.keys(log.changed_from as Record<string, unknown>) : [];
  const allChangedKeys = new Set([...changedToKeys, ...changedFromKeys]);
  const changedFields = Array.from(allChangedKeys);

  const filteredFields = changedFields.filter((field) => !isExcludedTimestampField(field));

  if (filteredFields.length > 0) {
    const displayFields = filteredFields
      .slice(0, 2)
      .map((field) => lowercasePreservingAcronyms(formatAttributeName(field)));
    const remainingCount = filteredFields.length - 2;
    return remainingCount > 0
      ? ` -> ${displayFields.join(', ')} +${remainingCount} more`
      : ` -> ${displayFields.join(', ')}`;
  }

  return '';
}

// ============================================================================
// Specialized Renderers
// ============================================================================

/**
 * Render activity for System-initiated Account events.
 */
export function renderAccountSystemActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log } = ctx;
  const passiveVerb = getPassiveVerb(log.action);

  let accountChangedFieldsText = '';
  if (log.action.toLowerCase() === 'updated') {
    accountChangedFieldsText = getChangedFieldsText(log);
  }

  return renderSystemEventActivity({
    entity: 'The account',
    passiveVerb,
    suffix:
      accountChangedFieldsText !== '' ? (
        <Text as="span" size="s" color="subtle">
          {accountChangedFieldsText}
        </Text>
      ) : undefined,
  });
}

/**
 * Render activity for System-initiated UserAccountPermissions events.
 */
export function renderUserAccountPermissionsSystemActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log, usersById } = ctx;
  const passiveVerb = getPassiveVerb(log.action);

  const data = log.data != null && typeof log.data === 'object' ? (log.data as Record<string, unknown>) : null;
  const permissionUserId = data != null ? (data.user_id as number | undefined) : undefined;
  const permissionUser = permissionUserId != null ? usersById.get(permissionUserId) : undefined;

  // Get user display info for tooltip (full string)
  let userDisplayText = `#${log.object_id}`;
  if (permissionUser != null) {
    userDisplayText = getUserDisplayNameWithEmail({
      email: permissionUser.email,
      first_name: permissionUser.first_name,
      last_name: permissionUser.last_name,
    });
  }

  // Render user name with email (name bold, email not bold)
  const userDisplayNode =
    permissionUser != null ? (
      <>
        {renderUserNameWithEmail({
          ...getUserNameAndEmail(permissionUser),
          suffix: "'s",
        })}
      </>
    ) : (
      <Text as="span" size="s" weight="medium">
        #{log.object_id}'s
      </Text>
    );

  return renderSystemEventActivity({
    entity: <>{userDisplayNode} account permissions</>,
    passiveVerb,
    hasHave: 'have',
    summaryText: `${userDisplayText}'s account permissions`,
  });
}

/**
 * Render activity for System-initiated WorkspacePermissions events.
 */
export function renderWorkspacePermissionsSystemActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log, workspaces } = ctx;
  const passiveVerb = getPassiveVerb(log.action);

  const workspaceId = Number(log.object_id);
  // Use Number() comparison to handle string/number type mismatches
  const workspace = workspaces.find((w) => Number(w.id) === workspaceId);
  const workspaceName = workspace != null ? workspace.name : `Workspace #${log.object_id}`;

  return renderSystemEventActivity({
    entity: (
      <>
        <Text as="span" size="s" weight="medium">
          {workspaceName}'s
        </Text>{' '}
        workspace permissions
      </>
    ),
    passiveVerb,
    hasHave: 'have',
    summaryText: `${workspaceName}'s workspace permissions`,
  });
}

/**
 * Render activity for System-initiated User (team member) events.
 */
export function renderUserSystemActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log } = ctx;
  const passiveVerb = getPassiveVerb(log.action);

  const teamMemberInfo = getTeamMemberInfo(log);
  if (teamMemberInfo.name == null && teamMemberInfo.email == null) {
    return null; // Fall through to generic handler
  }

  // Text for tooltip
  const teamMemberDisplayText =
    teamMemberInfo.name != null && teamMemberInfo.email != null
      ? `${teamMemberInfo.name} (${teamMemberInfo.email})`
      : teamMemberInfo.name ?? teamMemberInfo.email ?? '';

  // Render team member with proper formatting (name bold, email not bold)
  const teamMemberNode =
    teamMemberInfo.email != null ? (
      renderUserNameWithEmail({
        name: teamMemberInfo.name,
        email: teamMemberInfo.email,
      })
    ) : (
      <Text as="span" size="s" weight="medium">
        {teamMemberInfo.name}
      </Text>
    );

  return renderSystemEventActivity({
    entity: (
      <>
        <Text as="span" size="s" weight="medium">
          Team member:
        </Text>{' '}
        {teamMemberNode}
      </>
    ),
    passiveVerb,
    summaryText: `Team member: ${teamMemberDisplayText}`,
  });
}

/**
 * Render activity for System-initiated Role events.
 */
export function renderRoleSystemActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log, roleDefinitions } = ctx;
  const passiveVerb = getPassiveVerb(log.action);

  let roleName = `#${log.object_id}`;
  if (log.object_id != null && roleDefinitions != null) {
    const roleId = Number(log.object_id);
    const roleDef = roleDefinitions[roleId];
    if (roleDef != null) {
      roleName = `"${roleDef.name}"`;
    }
  }

  return renderSystemEventActivity({
    entity: `The ${roleName} role`,
    passiveVerb,
  });
}

/**
 * Render activity for ProfileDeletion events.
 */
export function renderProfileDeletionActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log, userName } = ctx;

  let deletedCount = 1;
  if (log.data != null && typeof log.data === 'object') {
    const data = log.data as Record<string, unknown>;
    if (Array.isArray(data.internal_ids) === true) {
      deletedCount = (data.internal_ids as unknown[]).length;
    }
  }
  const personText = deletedCount === 1 ? 'person' : 'people';
  const summary = `${userName} deleted ${deletedCount} ${personText}`;

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          deleted
        </Text>{' '}
        <Text as="span" size="s" weight="medium">
          {deletedCount} {personText}
        </Text>
      </Text>
    </Tooltip>
  );
}

/**
 * Render activity for ObjectDeletion events.
 */
export function renderObjectDeletionActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { log, userName } = ctx;

  const deletedCount = Array.isArray(log.data) === true ? (log.data as unknown[]).length : 1;

  if (deletedCount > 1) {
    const summary = `${userName} deleted ${deletedCount} objects`;
    return (
      <Tooltip content={summary}>
        <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
          {renderActorName(ctx)}{' '}
          <Text as="span" size="s" weight="medium">
            deleted
          </Text>{' '}
          <Text as="span" size="s" weight="medium">
            {deletedCount} objects
          </Text>
        </Text>
      </Tooltip>
    );
  }

  // Single deletion - show the object ID
  let objectId = '';
  if (Array.isArray(log.data) === true && (log.data as unknown[]).length > 0) {
    const firstItem = (log.data as unknown[])[0] as Record<string, unknown>;
    if (firstItem.id != null) {
      objectId = ` #${String(firstItem.id)}`;
    }
  }
  const summary = `${userName} deleted object${objectId}`;

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          deleted
        </Text>{' '}
        object
        <Text as="span" size="s" weight="medium">
          {objectId}
        </Text>
      </Text>
    </Tooltip>
  );
}

/**
 * Render activity for ProfileMerge events.
 */
export function renderProfileMergeActivity(ctx: ActivityRenderContext): React.ReactNode {
  const { userName } = ctx;
  const summary = `${userName} merged profiles`;

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          merged
        </Text>{' '}
        profiles
      </Text>
    </Tooltip>
  );
}

/**
 * Render activity for permission events (create/delete).
 */
export function renderPermissionActivity(
  ctx: ActivityRenderContext,
  permissionTargetMember: { displayName: string; name?: string | null; email?: string },
): React.ReactNode {
  const { log, userName } = ctx;
  const permissionVerb = log.action.toLowerCase() === 'created' ? 'set' : 'deleted';
  const permissionType = log.object_type === 'UserAccountPermissions' ? 'account permissions' : 'workspace permissions';
  const summary = `${userName} ${permissionVerb} ${permissionType} for ${permissionTargetMember.displayName}`;

  // Render target user with proper formatting (name bold, email not bold)
  const targetUserNode =
    permissionTargetMember.email != null ? (
      renderUserNameWithEmail({
        name: permissionTargetMember.name ?? null,
        email: permissionTargetMember.email,
      })
    ) : (
      <Text as="span" size="s" weight="medium">
        {permissionTargetMember.displayName}
      </Text>
    );

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          {permissionVerb}
        </Text>{' '}
        {permissionType} for {targetUserNode}
      </Text>
    </Tooltip>
  );
}

/**
 * Render activity for permission update events.
 */
export function renderPermissionUpdateActivity(
  ctx: ActivityRenderContext,
  permissionTargetMember: { displayName: string; name?: string | null; email?: string },
  changedFieldsText: string,
): React.ReactNode {
  const { log, userName, verb } = ctx;
  const permissionType = log.object_type === 'UserAccountPermissions' ? 'account permissions' : 'workspace permissions';
  const summary = `${userName} ${verb} the ${permissionType} for ${permissionTargetMember.displayName}${changedFieldsText}`;

  // Render target user with proper formatting (name bold, email not bold)
  const targetUserNode =
    permissionTargetMember.email != null ? (
      renderUserNameWithEmail({
        name: permissionTargetMember.name ?? null,
        email: permissionTargetMember.email,
      })
    ) : (
      <Text as="span" size="s" weight="medium">
        {permissionTargetMember.displayName}
      </Text>
    );

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          {verb}
        </Text>{' '}
        the {permissionType} for {targetUserNode}
        {changedFieldsText !== '' ? (
          <Text as="span" size="s" color="subtle">
            {changedFieldsText}
          </Text>
        ) : null}
      </Text>
    </Tooltip>
  );
}

/**
 * Render user activity (standard or updated).
 * Unified renderer for non-System user actions.
 */
export function renderUserActivity(
  ctx: ActivityRenderContext,
  options: {
    teamMemberInfo?: { name: string | null; email: string | null };
    objectIdentifierPart: string | React.ReactNode;
    changedFieldsText?: string;
  },
): React.ReactNode {
  const { userName, verb, displayObjectName } = ctx;
  const { teamMemberInfo, objectIdentifierPart, changedFieldsText = '' } = options;

  // Build team member text for tooltip
  // Include team member info when there's a name OR when there's an email without a name (email-only case)
  const teamMemberText =
    teamMemberInfo?.name != null
      ? `: ${teamMemberInfo.name}${teamMemberInfo.email != null ? ` (${teamMemberInfo.email})` : ''}`
      : teamMemberInfo?.email != null
      ? `: ${teamMemberInfo.email}`
      : '';

  // Build summary text for tooltip (extract text from React nodes)
  const objectIdentifierText =
    typeof objectIdentifierPart === 'string' ? objectIdentifierPart : extractTextFromReactNode(objectIdentifierPart);
  const summary = `${userName} ${verb}${
    displayObjectName !== '' ? ` ${displayObjectName}` : ''
  }${teamMemberText}${objectIdentifierText}${changedFieldsText}`;

  // Render team member part with proper formatting (name bold, email not)
  // Show when there's a name, OR when there's an email without a name (email-only case)
  const teamMemberNode =
    teamMemberInfo?.name != null || teamMemberInfo?.email != null ? (
      <>
        {': '}
        {teamMemberInfo.email != null ? (
          renderUserNameWithEmail({
            name: teamMemberInfo.name ?? null,
            email: teamMemberInfo.email,
          })
        ) : (
          <Text as="span" size="s" weight="medium">
            {teamMemberInfo.name}
          </Text>
        )}
      </>
    ) : null;

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          {verb}
        </Text>
        {displayObjectName !== '' ? (
          <>
            {' '}
            <Text as="span" size="s" weight="medium">
              {displayObjectName}
            </Text>
          </>
        ) : null}
        {teamMemberNode}
        {objectIdentifierPart !== '' && objectIdentifierPart != null ? (
          <Text as="span" size="s" weight="medium">
            {objectIdentifierPart}
          </Text>
        ) : null}
        {changedFieldsText !== '' ? (
          <Text as="span" size="s" color="subtle">
            {changedFieldsText}
          </Text>
        ) : null}
      </Text>
    </Tooltip>
  );
}

/**
 * Render activity for user updating their own profile.
 * Shows "X updated their profile" instead of "X updated team member: X"
 */
export function renderSelfUpdateActivity(ctx: ActivityRenderContext, changedFieldsText = ''): React.ReactNode {
  const { actorName, actorEmail, userName, verb } = ctx;

  // Build summary text for tooltip - use formatted name/email if available
  const actorDisplayText =
    actorName != null && actorEmail != null ? `${actorName} (${actorEmail})` : actorEmail ?? userName;
  const summary = `${actorDisplayText} ${verb} their profile${changedFieldsText}`;

  return (
    <Tooltip content={summary}>
      <Text size="s" style={ELLIPSIS_TEXT_STYLE}>
        {renderActorName(ctx)}{' '}
        <Text as="span" size="s" weight="medium">
          {verb}
        </Text>{' '}
        their profile
        {changedFieldsText !== '' ? (
          <Text as="span" size="s" color="subtle">
            {changedFieldsText}
          </Text>
        ) : null}
      </Text>
    </Tooltip>
  );
}

/**
 * Render generic system event activity.
 */
export function renderGenericSystemActivity(ctx: ActivityRenderContext, entityName: string | null): React.ReactNode {
  const { log, objectName, contextId } = ctx;
  const passiveVerb = getPassiveVerb(log.action);

  const lowercasedObjectName = lowercasePreservingAcronyms(objectName);

  // Determine if object name is plural
  const lowercasedName = objectName.toLowerCase();
  const isPluralObject =
    lowercasedName.endsWith('credentials') === true ||
    lowercasedName.endsWith('settings') === true ||
    lowercasedName.endsWith('permissions') === true;
  const hasHave = isPluralObject === true ? 'have' : 'has';

  // When entity name is available, show: The "Entity Name" object type has been added
  // Don't show identifier for Design Studio Global Styles
  const shouldHideObjectId = log.object_type === 'DesignStudioGlobalStyles';
  if (entityName != null && !shouldHideObjectId) {
    const objectIdentifier = getObjectIdentifier(log, entityName, contextId);
    const entityText =
      typeof objectIdentifier === 'string' ? (
        `The ${objectIdentifier} ${lowercasedObjectName}`
      ) : (
        <>
          The {objectIdentifier} {lowercasedObjectName}
        </>
      );
    return renderSystemEventActivity({
      entity: entityText,
      passiveVerb,
      hasHave,
      summaryText: `The "${entityName}" ${lowercasedObjectName}`,
    });
  }

  // Fall back to ID when no entity name (or when identifier should be hidden)
  const objectIdentifier = shouldHideObjectId ? null : getObjectIdentifier(log, null, contextId);
  const entityText =
    objectIdentifier == null ? (
      `The ${lowercasedObjectName}`
    ) : typeof objectIdentifier === 'string' ? (
      `The ${lowercasedObjectName} ${objectIdentifier}`
    ) : (
      <>
        The {lowercasedObjectName} {objectIdentifier}
      </>
    );

  // Extract text from React node for tooltip
  const objectIdentifierText =
    objectIdentifier == null
      ? ''
      : typeof objectIdentifier === 'string'
      ? objectIdentifier
      : extractTextFromReactNode(objectIdentifier);

  return renderSystemEventActivity({
    entity: entityText,
    passiveVerb,
    hasHave,
    summaryText: `The ${lowercasedObjectName}${objectIdentifierText !== '' ? ` ${objectIdentifierText}` : ''}`,
  });
}
